import { useCallback, useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import { EditorProps } from './types';
import Toolbar from './toolbar';
import './styles.css';

export default function Editor({
  id = 'performly-quill',
  error,
  simple = false,
  helperText,
  maxLength = 300, // Default max length
  isDefaultValue,  // Make sure to accept the value prop
  showLists = false,
  ...other
}: EditorProps) {
  const [charCount, setCharCount] = useState(0);
  const quillRef = useRef<ReactQuill | null | any>(null);

  // This effect makes sure that the char count is updated when value changes
  useEffect(() => {
    if (isDefaultValue) {
      const quill = quillRef.current?.getEditor();
      if (quill) {
        const text = quill.getText().trim();
        setCharCount(text.length);
      }
    }
  }, [isDefaultValue]); // Whenever the value prop changes, update char count


  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      quill.focus();
      quill.on('text-change', handleTextChange);
    }
    return () => {
      quill?.off('text-change', handleTextChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTextChange = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const text = quill.getText().trim();
    if (text.length >= maxLength) {
      quill.deleteText(maxLength, text.length);
    }

    // Get the document contents
    const contents = quill.getContents();

    // Remove any list attributes (bullets or numbers)
    contents.ops.forEach((op: any) => {
      if (op.attributes?.list) {
        delete op.attributes.list;
      }
    });

    setCharCount(text.length >= maxLength ? maxLength : text.length);
  }, [maxLength]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={other.containerClassName}>
      <div className={other.editorClassName}>
        <Toolbar id={id} isSimple={simple} showLists={showLists} />

        <ReactQuill
          modules={{
            toolbar: {
              container: `#${id}`,
            },
          }}
          formats={showLists ? ['bold', 'italic', 'underline', 'list'] : ['bold', 'italic', 'underline']} // Removed 'list'
          placeholder="Add description"
          className="h-[calc(100%-2.625rem)] !normal-case"
          ref={quillRef}
          {...other}
        />
      </div>
      <div className='flex justify-between'>
        <div>
          {helperText && helperText}
        </div>

        {/* Character Counter */}
        <div style={{ textAlign: 'right', padding: '4px', color: charCount > maxLength ? 'red' : 'gray' }}>
          {charCount}/{maxLength}
        </div>
      </div>
    </div>
  );
}
